// apps/web/src/App.tsx
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Auth, Logger } from "aws-amplify";

import PageWrapper from "./library/toggle";
import { ConfigurationPage } from "./pages/configuration-sidebar";
import { AIAgentSidebar } from "./pages/agent-api-sidebar";
import { AIAgentNewChat } from "./pages/agent-api-new-chat";
import { AIAgentViewChat } from "./pages/agent-api-view-chat";
import { PassToken } from "./pages/pass-token";

// ↓ Keep logs quiet so you can spot your own debug lines
Logger.LOG_LEVEL = "WARN";

// --- Mount beacons to prove which components actually render ---
function DebugMount({ name }: { name: string }) {
  useEffect(() => {
    const ts = new Date().toISOString();
    (window as any)[`DEBUG_MOUNT_${name}`] = ts;
    sessionStorage.setItem(`debug-mounted-${name}`, ts);
    console.log(`[DebugMount] mounted ${name} @ ${ts}`);
    return () => console.log(`[DebugMount] unmounted ${name}`);
  }, [name]);
  return null;
}

function RouteProbe({
  name,
  children,
}: {
  name: string;
  children: ReactNode;
}) {
  useEffect(() => {
    console.log(`[RouteProbe] rendering route: ${name}`);
  }, [name]);
  return (
    <>
      <DebugMount name={name} />
      {children}
    </>
  );
}

const ROUTES_VERSION = "ROUTES_v3.1_2025-08-18";
console.log(`[Routes] boot ${ROUTES_VERSION}`);

function App() {
  const location = useLocation();
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  const isPassTokenRoute = location.pathname === "/pass-token";

  useEffect(() => {
    console.log("🔐 useEffect: Auth check on path", location.pathname);
    if (isPassTokenRoute) return;

    Auth.currentAuthenticatedUser()
      .then(() => {
        console.log("✅ Authenticated");
        setIsSignedIn(true);
      })
      .catch(() => {
        console.log("❌ Not authenticated");
        setIsSignedIn(false);
      });
  }, [isPassTokenRoute, location.pathname]);

  useEffect(() => {
    const justRedirected = sessionStorage.getItem("just-signed-in") === "true";

    console.log("🔁 Auth redirect check", {
      isSignedIn,
      justRedirected,
      pathname: location.pathname,
    });

    if (isSignedIn === false && !justRedirected && !isPassTokenRoute) {
      sessionStorage.setItem("just-signed-in", "initiated");
      Auth.federatedSignIn();
    }

    if (justRedirected) {
      sessionStorage.removeItem("just-signed-in");
    }
  }, [isSignedIn, isPassTokenRoute, location.pathname]);

  if (!isPassTokenRoute && isSignedIn === null) {
    console.log("⏳ Waiting for auth to resolve");
    return (
      <div className="flex items-center justify-center h-screen text-lg">
        Loading authentication...
      </div>
    );
  }

  if (isPassTokenRoute) {
    console.log("📍 Entered /pass-token route");
    return (
      <PageWrapper hideToggle>
        <DebugMount name="PassTokenRoute" />
        <Routes location={location} key={location.pathname}>
          <Route
            path="/pass-token"
            element={<PassToken onSessionReady={() => setIsSignedIn(true)} />}
          />
          <Route path="*" element={<Navigate to="/pass-token" replace />} />
        </Routes>
      </PageWrapper>
    );
  }

  console.log("🧭 Rendering full app routes", {
    isSignedIn,
    pathname: location.pathname,
    ROUTES_VERSION,
  });

  return (
    <PageWrapper>
      <DebugMount name="AppRoutesRoot" />
      <Routes location={location} key={location.pathname}>
        <Route path="/configuration" element={<ConfigurationPage />} />

        <Route path="/chat" element={<AIAgentSidebar />}>
          {/* /chat -> /chat/new */}
          <Route index element={<Navigate to="/chat/new" replace />} />

          {/* NEW CHAT */}
          <Route
            path="new"
            element={
              <RouteProbe name="AIAgentNewChat">
                <AIAgentNewChat />
              </RouteProbe>
            }
          />

          {/* VIEW CHAT */}
          <Route
            path="view/:chatId"
            element={
              <RouteProbe name="AIAgentViewChat">
                <AIAgentViewChat />
              </RouteProbe>
            }
          />

          {/* Optional guard for /chat/view without :chatId */}
          <Route path="view" element={<Navigate to="/chat/new" replace />} />
        </Route>

        {/* Root — signed-in users land in /chat/new, otherwise /pass-token if in OAuth dance */}
        <Route
          path="/"
          element={
            isSignedIn ? (
              <Navigate to="/chat/new" replace />
            ) : window.location.search.includes("code=") ? (
              <Navigate to="/pass-token" replace />
            ) : (
              <div>Redirecting to sign-in...</div>
            )
          }
        />

        <Route
          path="*"
          element={
            <div style={{ padding: 24 }}>
              404 - Unknown Route
              <br />
              <br />
              <a href="/chat/new">Go Home</a>
            </div>
          }
        />
      </Routes>
    </PageWrapper>
  );
}

export default App;
